import React from 'react';
import { BrowserRouter as Router, Routes, Route, useParams, useNavigate, useLocation, Navigate } from 'react-router-dom';
import ContentPage from './pages/ContentPage';
import ChatComponent from './components/ChatComponent';
import { SocketProvider } from './context/SocketProvider';
import VoiceClientPage from './pages/VoiceClientPage';
import { v4 as uuidv4 } from 'uuid'; // Import uuid
import { DataProvider } from './context/DataContext'; // Import DataProvider
import ServicePage from './pages/ServicePage'; // Import ServicePage
import CookieBanner from './components/CookieBanner'; // Import your CookieBanner component
import { LanguageProvider } from './context/LanguageContext'; // Import LanguageProvider
import { getLanguageFileName, getLanguageFromPath } from './utils/languageUtils'; // Import language utilities


const LoadContent = () => {
  const { lang, pageId } = useParams();
  const location = useLocation();
  const [pageData, setPageData] = React.useState(null);

  React.useEffect(() => {
    // Determine language: from URL param or default to 'en'
    const currentLang = lang || getLanguageFromPath(location.pathname);
    const fileName = getLanguageFileName(pageId, currentLang);

    // Try to load language-specific file
    import(`./data/${fileName}`)
      .then(module => {
        setPageData(module.default);
      })
      .catch(() => {
        // Fallback to English if language-specific file not found
        if (currentLang !== 'en') {
          import(`./data/${pageId}.json`)
            .then(module => {
              setPageData(module.default);
            })
            .catch(() => {
              setPageData(null);
            });
        } else {
          setPageData(null);
        }
      });
  }, [lang, pageId, location.pathname]);

  return pageData ? <ContentPage data={pageData} /> : <div>Loading...</div>;
};

const App = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Set a static page title
  React.useEffect(() => {
    document.title = "ivasi";
  }, []);

  const [sessionId] = React.useState(() => {
    const match = location.pathname.match(/^\/voiceClient\/(.+)/);
    if (match && match[1]) {
      console.log('SessionID:', match[1]);
      return match[1];
    } else {
      const newSessionId = uuidv4();
      console.log('Generated SessionID:', newSessionId);
      return newSessionId;
    }
  });

  const handleNavigate = React.useCallback((path) => {
    navigate(path);
  }, [navigate]);

  const clientType = location.pathname.startsWith('/voiceClient') 
    ? 'chat' 
    : location.pathname.startsWith('/service') 
      ? 'service' 
      : 'content';

  if (!sessionId) {
    return <div>Loading...</div>;
  }

  return (
    <SocketProvider sessionId={sessionId} type={clientType} onNavigate={handleNavigate}>
      <LanguageProvider>
        <Routes>
          <Route path="/voiceClient/:SessionID" element={<VoiceClientPage />} />
          <Route path="/service/:id" element={<ServicePage />} />
          <Route path="/:lang/:pageId" element={<LoadContent />} />
          <Route path="/:pageId" element={<LoadContent />} />
          <Route path="/" element={<Navigate to="/main" replace />} />
        </Routes>
        <ChatComponent sessionId={sessionId} /> {/* Pass sessionId as a prop */}
        <CookieBanner /> {/* Include the CookieBanner component */}
      </LanguageProvider>
    </SocketProvider>
  );
};

export default function AppWrapper() {
  return (
    <Router>
      <DataProvider models={["dolphin", "sealion_7"]}>
        <App />
      </DataProvider>
    </Router>
  );
}